import React, { useEffect, useState } from 'react';
import { taskApi, projectApi, managerApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function TasksPage() {
  const { isManager, isAdmin, isHR } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('my');
  const [showForm, setShowForm] = useState(false);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [message, setMessage] = useState(null);
  const emptyForm = { title: '', description: '', priority: 'MEDIUM', status: 'TODO', projectId: '', assignedToId: '', dueDate: '', estimatedHours: '' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    projectApi.activeOnly().then(r => r.data.success ? setProjects(r.data.data || []) : null).catch(() => {});
    if (isManager || isAdmin || isHR) {
      managerApi.getTeam().then(r => r.data.success ? setTeamMembers(r.data.data || []) : null).catch(() => {});
    }
  }, []);

  useEffect(() => { loadTasks(); }, [view]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = view === 'team' ? await taskApi.getTeamTasks() : await taskApi.getMyTasks();
      if (res.data.success) setTasks(res.data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.projectId === '') payload.projectId = null;
      if (payload.assignedToId === '') payload.assignedToId = null;
      else payload.assignedToId = parseInt(payload.assignedToId);
      if (payload.estimatedHours === '') payload.estimatedHours = null;
      else payload.estimatedHours = parseFloat(payload.estimatedHours);
      if (payload.dueDate === '') payload.dueDate = null;

      if (editId) {
        await taskApi.update(editId, payload);
        setMessage({ type: 'success', text: 'Task updated successfully!' });
      } else {
        await taskApi.create(payload);
        setMessage({ type: 'success', text: 'Task created successfully!' });
      }
      setShowForm(false);
      setEditId(null);
      setForm(emptyForm);
      loadTasks();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Error saving task' });
    }
  };

  const editTask = (t) => {
    setEditId(t.id);
    setForm({
      title: t.title || '',
      description: t.description || '',
      priority: t.priority || 'MEDIUM',
      status: t.status || 'TODO',
      projectId: t.projectId || '',
      assignedToId: t.assignedToId || '',
      dueDate: t.dueDate || '',
      estimatedHours: t.estimatedHours || '',
    });
    setShowForm(true);
  };

  const updateStatus = async (taskId, newStatus) => {
    try {
      await taskApi.update(taskId, { status: newStatus });
      loadTasks();
    } catch (err) {
      alert('Error updating status');
    }
  };

  const priorityBadge = (p) => {
    const map = { CRITICAL: 'badge-danger', HIGH: 'badge-warning', MEDIUM: 'badge-info', LOW: 'badge-gray' };
    return map[p] || 'badge-gray';
  };

  const statusBadge = (s) => {
    const map = { TODO: 'badge-gray', IN_PROGRESS: 'badge-info', IN_REVIEW: 'badge-warning', COMPLETED: 'badge-success', CANCELLED: 'badge-danger' };
    return map[s] || 'badge-gray';
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Tasks</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn ${view === 'my' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setView('my')}>My Tasks</button>
          {(isManager || isAdmin || isHR) && (
            <button className={`btn ${view === 'team' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setView('team')}>Team Tasks</button>
          )}
          <button className="btn btn-success" onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(true); }}>+ New Task</button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: 16 }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>✕</button>
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <h3>{editId ? 'Edit Task' : 'New Task'}</h3>
            <button className="btn btn-sm btn-outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
          </div>
          <div className="card-body">
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Title *</label>
                <input className="form-control" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Project</label>
                  <select className="form-control" value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}>
                    <option value="">— No Project —</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.projectCode} – {p.projectName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Assign To</label>
                  <select className="form-control" value={form.assignedToId} onChange={e => setForm({ ...form, assignedToId: e.target.value })}>
                    <option value="">— Myself —</option>
                    {teamMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.firstName} {m.lastName} ({m.employeeId})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Priority</label>
                  <select className="form-control" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input className="form-control" type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Estimated Hours</label>
                  <input className="form-control" type="number" step="0.5" min="0" value={form.estimatedHours} onChange={e => setForm({ ...form, estimatedHours: e.target.value })} />
                </div>
              </div>
              {editId && (
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="TODO">To Do</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="IN_REVIEW">In Review</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-control" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editId ? 'Update Task' : 'Create Task'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Project</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Due Date</th>
                <th>Hours</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr><td colSpan="9" className="empty-state">No tasks found</td></tr>
              ) : tasks.map(t => (
                <tr key={t.id}>
                  <td><strong>{t.taskCode}</strong></td>
                  <td>{t.title}</td>
                  <td style={{ fontSize: 13 }}>{t.projectName || '—'}</td>
                  <td><span className={`badge ${priorityBadge(t.priority)}`}>{t.priority}</span></td>
                  <td>
                    <select className="form-control" style={{ fontSize: 12, padding: '2px 6px', width: 120 }}
                      value={t.status} onChange={e => updateStatus(t.id, e.target.value)}>
                      <option value="TODO">To Do</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="IN_REVIEW">In Review</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </td>
                  <td style={{ fontSize: 13 }}>{t.assignedToName || '—'}</td>
                  <td style={{ fontSize: 13 }}>{t.dueDate || '—'}</td>
                  <td style={{ fontSize: 13 }}>{t.actualHours || 0}/{t.estimatedHours || '—'}</td>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => editTask(t)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
